'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'

import Close from '../../icons/Close'
import { cn } from '../../utils/cn'
import { Button } from '../_base/Button'
import { ScrollFade } from '../feedback/ScrollFade'

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-lg',
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  closeOnEscape = false,
  fullScreenMobile = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  closeOnEscape?: boolean
  fullScreenMobile?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // Base layout & shared animations
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 px-lg py-lg fixed z-50 grid overflow-x-hidden overflow-y-auto border shadow-lg duration-200 outline-none',
          // Mobile animations — slide for full-screen, zoom for centered
          fullScreenMobile &&
            'max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom',
          !fullScreenMobile &&
            'max-md:data-[state=closed]:zoom-out-95 max-md:data-[state=open]:zoom-in-95',
          // Desktop animation
          'md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95',
          // Mobile layout — full-screen or centered
          fullScreenMobile
            ? 'max-md:inset-0 max-md:h-full max-md:max-h-full max-md:w-full max-md:max-w-full max-md:rounded-none'
            : 'max-md:top-[50%] max-md:left-[50%] max-md:max-h-[min(750px,90vh)] max-md:w-full max-md:max-w-[calc(100%-2rem)] max-md:translate-x-[-50%] max-md:translate-y-[-50%] max-md:rounded-2xl',
          // Desktop layout
          'md:top-[50%] md:left-[50%] md:max-h-[min(750px,90vh)] md:w-full md:max-w-lg md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-2xl',
          // Slot overrides
          'has-[:where([data-slot="dialog-footer"])]:pb-0 has-[:where([data-slot="dialog-header"])]:pt-0',
          className
        )}
        onEscapeKeyDown={event => {
          if (!closeOnEscape) {
            // Prevent the dialog from closing
            event.preventDefault()

            // Clear the search input if it is focused
            if (event.target instanceof HTMLInputElement && event.target.type === 'search') {
              // We can't use event.target.value = '' because it will not trigger the input event
              Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(
                event.target,
                ''
              )
              event.target.dispatchEvent(new Event('input', { bubbles: true }))
            }
          }
        }}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<'div'> & { showCloseButton?: boolean }) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'gap-xs bg-popover py-lg sticky top-0 isolate z-10 mb-0! flex flex-col text-center sm:text-left',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          data-slot="dialog-close"
          className="top-lg absolute right-0 -translate-y-[4px]"
          asChild
        >
          <Button variant="ghost" size="icon" data-testid="dialog-close-button">
            <Close />
          </Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

// `-mx-lg px-lg` cancels DialogContent's padding so the solid background and the fade reach both
// dialog edges, then re-insets the content. Without it the gutters stay uncovered and content
// scrolling under the footer shows through them.
function DialogFooter({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'py-lg bg-popover -mx-lg px-lg sticky bottom-0 z-10 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between',
        className
      )}
      {...props}
    >
      <ScrollFade className="from-popover" />
      {children}
    </div>
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-title-xs text-popover-foreground pr-12', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-2xs', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
}
